import { achievements } from "../content/meta";
import type { GameSettings, RunResult, SaveData } from "../types/game";

export const SAVE_KEY = "aeralume-save-v3";
const DB_NAME = "aeralume-riftwardens";
const STORE_NAME = "saves";
let memorySave: SaveData | null = null;

const now = (): string => new Date().toISOString();

export const defaultSettings = (): GameSettings => ({
  language: ((navigator.language || "en").toLowerCase().startsWith("bs") ? "bs" : (navigator.language || "en").toLowerCase().startsWith("de") ? "de" : "en"),
  masterVolume: 0.8,
  musicVolume: 0.52,
  effectsVolume: 0.72,
  reducedEffects: (navigator.deviceMemory ?? 8) <= 4,
  reducedMotion: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
  screenShake: true,
  colorblind: false,
  uiScale: 1,
});

export function createSave(): SaveData {
  const created = now();
  return {
    version: 3,
    revision: 1,
    createdAt: created,
    updatedAt: created,
    unlockedLevel: 1,
    selectedHero: "brann",
    unlockedHeroes: ["brann"],
    unlockedTowers: ["rangers"],
    unlockedSpells: ["starfall"],
    levelRecords: {},
    upgradeRanks: {},
    spentStars: 0,
    achievements: {},
    discoveredEnemies: [],
    tutorialFlags: [],
    stats: { kills: 0, towersBuilt: 0, towersUpgraded: 0, spellsCast: 0, bossesDefeated: 0, wins: 0, perfectWins: 0, playSeconds: 0 },
    settings: defaultSettings(),
    lastLevelId: "level-01",
  };
}

interface LegacySave {
  version?: number;
  unlockedLevel?: number;
  selectedHero?: string;
  levelRecords?: SaveData["levelRecords"];
  upgrades?: Record<string, number>;
  upgradeRanks?: Record<string, number>;
  settings?: Partial<GameSettings>;
  [key: string]: unknown;
}

export function migrateSave(raw: unknown): SaveData {
  const base = createSave();
  if (!raw || typeof raw !== "object") return base;
  const legacy = raw as LegacySave;
  const candidate = { ...base, ...legacy } as SaveData;
  candidate.version = 3;
  candidate.revision = Number.isFinite(candidate.revision) ? Math.max(1, candidate.revision) : 1;
  candidate.unlockedLevel = Math.max(1, Math.min(20, Number(candidate.unlockedLevel) || 1));
  candidate.upgradeRanks = { ...(legacy.upgrades ?? {}), ...(legacy.upgradeRanks ?? {}) };
  candidate.settings = { ...base.settings, ...(legacy.settings ?? {}) };
  candidate.settings.language = ["en", "bs", "de"].includes(candidate.settings.language) ? candidate.settings.language : "en";
  candidate.settings.uiScale = Math.max(0.85, Math.min(1.25, Number(candidate.settings.uiScale) || 1));
  candidate.unlockedHeroes = Array.isArray(candidate.unlockedHeroes) ? [...new Set(candidate.unlockedHeroes.filter((value): value is string => typeof value === "string"))] : ["brann"];
  candidate.unlockedTowers = Array.isArray(candidate.unlockedTowers) ? [...new Set(candidate.unlockedTowers)] : ["rangers"];
  candidate.unlockedSpells = Array.isArray(candidate.unlockedSpells) ? [...new Set(candidate.unlockedSpells.filter((value): value is string => typeof value === "string"))] : ["starfall"];
  candidate.discoveredEnemies = Array.isArray(candidate.discoveredEnemies) ? [...new Set(candidate.discoveredEnemies.filter((value): value is string => typeof value === "string"))] : [];
  candidate.tutorialFlags = Array.isArray(candidate.tutorialFlags) ? [...new Set(candidate.tutorialFlags.filter((value): value is string => typeof value === "string"))] : [];
  candidate.stats = { ...base.stats, ...(candidate.stats ?? {}) };
  candidate.achievements = candidate.achievements && typeof candidate.achievements === "object" ? candidate.achievements : {};
  candidate.levelRecords = candidate.levelRecords && typeof candidate.levelRecords === "object" ? candidate.levelRecords : {};
  return candidate;
}

export function storageAvailable(): boolean {
  try {
    const key = "__aeralume_test__";
    localStorage.setItem(key, key);
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function loadSave(): SaveData {
  if (memorySave) return structuredClone(memorySave);
  if (!storageAvailable()) {
    memorySave = createSave();
    return structuredClone(memorySave);
  }
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    memorySave = raw ? migrateSave(JSON.parse(raw)) : createSave();
  } catch {
    memorySave = createSave();
  }
  return structuredClone(memorySave);
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) return reject(new Error("IndexedDB unavailable"));
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function mirrorToIndexedDb(save: SaveData): Promise<void> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(save, "primary");
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
    db.close();
  } catch {
    // localStorage remains the durable fallback.
  }
}

export async function hydrateFromIndexedDb(current: SaveData): Promise<SaveData> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(STORE_NAME, "readonly");
    const request = transaction.objectStore(STORE_NAME).get("primary");
    const stored = await new Promise<unknown>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    db.close();
    const migrated = migrateSave(stored);
    if (stored && migrated.revision > current.revision) {
      persistSave(migrated);
      return migrated;
    }
  } catch {
    // The synchronous local save remains valid.
  }
  return current;
}

export function persistSave(save: SaveData): SaveData {
  const next = structuredClone(save);
  next.version = 3;
  next.revision += 1;
  next.updatedAt = now();
  memorySave = next;
  try { localStorage.setItem(SAVE_KEY, JSON.stringify(next)); } catch { /* memory fallback */ }
  void mirrorToIndexedDb(next);
  return structuredClone(next);
}

export const totalStars = (save: SaveData): number => Object.values(save.levelRecords).reduce((sum, record) => sum + (record.stars || 0), 0);
export const challengeCount = (save: SaveData): number => Object.values(save.levelRecords).reduce((sum, record) => sum + Object.values(record.challenges ?? {}).filter(Boolean).length - (record.challenges?.campaign ? 1 : 0), 0);
export const purchasedUpgradeCount = (save: SaveData): number => Object.keys(save.upgradeRanks).length;

export function achievementProgress(save: SaveData, id: string): number {
  const achievement = achievements.find((entry) => entry.id === id);
  if (!achievement) return 0;
  const value = achievement.metric === "stars" ? totalStars(save)
    : achievement.metric === "challenges" ? challengeCount(save)
    : achievement.metric === "upgrades" ? purchasedUpgradeCount(save)
    : achievement.metric === "perfect" ? save.stats.perfectWins
    : achievement.metric === "bosses" ? save.stats.bossesDefeated
    : achievement.metric === "towers" ? save.stats.towersBuilt
    : achievement.metric === "spells" ? save.stats.spellsCast
    : achievement.metric === "kills" ? save.stats.kills
    : save.stats.wins;
  return Math.min(achievement.target, value);
}

export function applyRunResult(save: SaveData, result: RunResult): SaveData {
  const next = structuredClone(save);
  next.lastLevelId = result.levelId;
  next.stats.kills += result.kills;
  next.stats.towersBuilt += result.towersBuilt;
  next.stats.towersUpgraded += result.towersUpgraded;
  next.stats.spellsCast += result.spellsCast;
  next.stats.playSeconds += result.elapsedSeconds;
  next.discoveredEnemies = [...new Set([...next.discoveredEnemies, ...result.discovered])];
  if (result.victory) {
    const levelNumber = Number(result.levelId.split("-")[1]);
    next.unlockedLevel = Math.max(next.unlockedLevel, Math.min(20, levelNumber + 1));
    next.stats.wins += 1;
    if (result.stars === 3) next.stats.perfectWins += 1;
    if (result.bossDefeated) next.stats.bossesDefeated += 1;
    const previous = next.levelRecords[result.levelId] ?? { stars: 0, bestLives: 0, scores: {}, challenges: {} };
    next.levelRecords[result.levelId] = {
      stars: Math.max(previous.stars, result.stars),
      bestLives: Math.max(previous.bestLives, result.lives),
      scores: { ...previous.scores, [result.difficulty]: Math.max(previous.scores[result.difficulty] ?? 0, result.score) },
      challenges: { ...previous.challenges, [result.mode]: true },
    };
    if (levelNumber >= 2) next.unlockedTowers = [...new Set<import("../types/game").TowerFamily>([...next.unlockedTowers, "arcanists"])];
    if (levelNumber >= 3) next.unlockedTowers = [...new Set<import("../types/game").TowerFamily>([...next.unlockedTowers, "artillery"])];
    if (levelNumber >= 4) next.unlockedTowers = [...new Set<import("../types/game").TowerFamily>([...next.unlockedTowers, "guardians"])];
    if (levelNumber >= 4) next.unlockedHeroes = [...new Set([...next.unlockedHeroes, "ilyra"])];
    if (levelNumber >= 8) next.unlockedHeroes = [...new Set([...next.unlockedHeroes, "nym"])];
    if (levelNumber >= 12) next.unlockedHeroes = [...new Set([...next.unlockedHeroes, "puck"])];
    if (levelNumber >= 4) next.unlockedSpells = [...new Set([...next.unlockedSpells, "wardlings"])];
    if (levelNumber >= 8) next.unlockedSpells = [...new Set([...next.unlockedSpells, "timebloom"])];
  }
  for (const achievement of achievements) next.achievements[achievement.id] = achievementProgress(next, achievement.id);
  return persistSave(next);
}

export function exportSave(save: SaveData): string {
  const payload = JSON.stringify({ product: "aeralume-riftwardens", exportedAt: now(), save });
  const bytes = new TextEncoder().encode(payload);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `AERALUME3.${btoa(binary)}`;
}

export function importSave(text: string): SaveData {
  const trimmed = text.trim();
  if (!trimmed.startsWith("AERALUME3.")) throw new Error("Unsupported save format");
  const binary = atob(trimmed.slice("AERALUME3.".length));
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  const payload = JSON.parse(new TextDecoder().decode(bytes)) as { product?: string; save?: unknown };
  if (payload.product !== "aeralume-riftwardens" || !payload.save) throw new Error("Wrong product");
  return persistSave(migrateSave(payload.save));
}

export function resetSave(keepSettings?: GameSettings): SaveData {
  const fresh = createSave();
  if (keepSettings) fresh.settings = keepSettings;
  memorySave = null;
  try { localStorage.removeItem(SAVE_KEY); } catch { /* memory fallback */ }
  return persistSave(fresh);
}
